// Simple electronic signature (Oct 2026, idea 33) — the CRM side: send a document for signing (content from the CRM,
// the same sections as the print service), follow its status on the client's documents tab, and bring the signed PDF
// back into the client's vault (domain command recordSignedDocument) as soon as it is signed.
import { useEffect, useState } from 'react';
import { esignDocumentBlocks, recordSignedDocument, signedDocumentId, newId, type ESignKind } from '@vero/domain';
import { PlatformError, type ESignInfo } from '@vero/platform';
import { useRuntime, type Runtime } from '../core/runtime.tsx';
import { Field, Modal } from '../core/ui.tsx';
import { Icon } from '../core/icons.tsx';
import { accountService } from '../core/account-service.ts';
import { useAccount } from './account.tsx';

type Ev = { target: HTMLInputElement };
type SelEv = { target: HTMLSelectElement };
const KINDS: ESignKind[] = ['needs', 'consent', 'proposal', 'other'];
const b64ToBytes = (s: string) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));
async function sha256Hex(bytes: Uint8Array) { return [...new Uint8Array(await crypto.subtle.digest('SHA-256', bytes as BufferSource))].map((b) => b.toString(16).padStart(2, '0')).join(''); }

/** a signed document not yet in the vault → stored locally, recorded, acknowledged (safe to run again) */
export async function collectSigned(rt: Runtime, e: ESignInfo): Promise<boolean> {
  const api = accountService()?.api;
  if (!api || e.status !== 'signed' || !e.personId || !rt.view.get('persons', e.personId)) return false;
  if (rt.view.get('documents', signedDocumentId(e.id))) { if (!e.collectedAt) await api.esignCollected(e.id).catch(() => undefined); return false; }
  const f = await api.esignSigned(e.id);
  const bytes = b64ToBytes(f.base64);
  if ((await sha256Hex(bytes)) !== f.sha256) return false; // damaged in transit: try again later
  const fileId = newId();
  await rt.ws.adapter.putFile({ id: fileId, name: f.filename, mime: 'application/pdf', size: bytes.byteLength, sha256: f.sha256, createdAt: new Date().toISOString() }, bytes);
  const cs = await rt.ws.run((v, c) => recordSignedDocument(v, { esignId: e.id, personId: e.personId!, title: e.title, kind: e.kind as ESignKind, fileId, filename: f.filename, size: bytes.byteLength, sha256: f.sha256, signedAt: f.signedAt, originalSha256: e.docSha256 }, c)).catch(() => null);
  if (!cs) { await rt.ws.adapter.deleteFile(fileId); return false; }
  await api.esignCollected(e.id).catch(() => undefined);
  return true;
}

/** app shell: while signed in, signed documents come into the vault (at start, on focus, every 5 minutes) */
export function ESignWatcher() {
  const rt = useRuntime();
  const a = useAccount();
  useEffect(() => {
    if (!a?.signedIn || rt.ws.readOnlyReason) return;
    let stopped = false, running = false;
    const pull = async () => {
      if (stopped || running) return;
      running = true;
      try {
        const { items } = await accountService()!.api.esignList();
        let n = 0;
        for (const e of items.filter((x) => x.status === 'signed' && !x.collectedAt)) if (await collectSigned(rt, e)) n++;
        if (n) rt.toast(rt.t('esign.collected', { n }), 'success');
      } catch { /* offline, signed out or not configured */ } finally { running = false; }
    };
    const first = setTimeout(() => void pull(), 3000);
    const every = setInterval(() => void pull(), 5 * 60_000);
    const onFocus = () => void pull();
    window.addEventListener('focus', onFocus);
    return () => { stopped = true; clearTimeout(first); clearInterval(every); window.removeEventListener('focus', onFocus); };
  }, [a?.signedIn]);
  return null;
}

/** "Send for signature" for one client */
export function SendForSignature({ personId, onSent }: { personId: string; /** AVRASTRO Video: the link also goes into the call chat */ onSent?: (url: string) => void }) {
  const rt = useRuntime();
  const { t, view } = rt;
  const p = view.get('persons', personId);
  const [kind, setKind] = useState<ESignKind>('needs');
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [email, setEmail] = useState(p?.primaryEmail ?? '');
  const [days, setDays] = useState(7);
  const [lang, setLang] = useState<'el' | 'en'>(rt.locale === 'en' ? 'en' : 'el');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<{ url: string; emailed: boolean } | null>(null);
  if (!p) return null;
  const defaultTitle = t(`esign.kind.${kind}`);
  const send = async () => {
    setBusy(true);
    try {
      const blocks = esignDocumentBlocks(view, personId, kind, (k, params) => rt.t(k, params), { text });
      const r = await accountService()!.api.esignCreate({ kind, title: title.trim() || defaultTitle, lang, personId, client: { name: p.displayName, email: email.trim() || null, phone: p.primaryPhone ?? null }, blocks, days });
      setDone({ url: r.url, emailed: r.emailed });
      onSent?.(r.url);
    } catch (e) { rt.toast(e instanceof PlatformError ? t(`esign.err.${e.message}`) === `esign.err.${e.message}` ? t('account.err.generic', { code: e.message }) : t(`esign.err.${e.message}`) : String(e), 'error'); } finally { setBusy(false); }
  };
  if (done) return (
    <Modal title={t('esign.sent_title')} footer={<button className="btn btn-primary" onClick={rt.close}>{t('common.done')}</button>}>
      <p className="small">{t(done.emailed ? 'esign.sent_emailed' : 'esign.sent_link', { email })}</p>
      <div className="row" style={{ gap: 8 }}><input className="input grow" readOnly value={done.url} data-testid="esign-url" /><button className="btn btn-sm" onClick={() => void navigator.clipboard?.writeText(done.url).then(() => rt.toast(t('common.copied'), 'success'))}><Icon name="copy" />{t('common.copy')}</button></div>
      <p className="tiny muted">{t('esign.legal')}</p>
    </Modal>
  );
  return (
    <Modal title={t('esign.send_title', { name: p.displayName })} footer={<><button className="btn" onClick={rt.close}>{t('common.cancel')}</button><button className="btn btn-primary" disabled={busy || !email.trim()} onClick={() => void send()} data-testid="esign-send">{t('esign.send')}</button></>}>
      <div className="form-grid">
        <Field label={t('esign.kind_label')}><select className="input" value={kind} onChange={(e: SelEv) => setKind(e.target.value as ESignKind)} data-testid="esign-kind">{KINDS.map((k) => <option key={k} value={k}>{t(`esign.kind.${k}`)}</option>)}</select></Field>
        <Field label={t('esign.lang')}><select className="input" value={lang} onChange={(e: SelEv) => setLang(e.target.value as 'el' | 'en')}><option value="el">Ελληνικά</option><option value="en">English</option></select></Field>
        <Field label={t('esign.title_label')} span2><input className="input" value={title} placeholder={defaultTitle} onChange={(e: Ev) => setTitle(e.target.value)} /></Field>
        <Field label={t('esign.extra_text')} span2 hint={t('esign.extra_hint')}><textarea className="input" rows={3} value={text} onChange={(e: { target: HTMLTextAreaElement }) => setText(e.target.value)} /></Field>
        <Field label={t('esign.email')} hint={t('esign.email_hint')}><input className="input" type="email" value={email} onChange={(e: Ev) => setEmail(e.target.value)} data-testid="esign-email" /></Field>
        <Field label={t('esign.days')}><select className="input" value={days} onChange={(e: SelEv) => setDays(Number(e.target.value))}>{[1, 3, 7, 14, 30].map((d) => <option key={d} value={d}>{t('esign.days_n', { n: d })}</option>)}</select></Field>
      </div>
      <p className="callout small" style={{ marginTop: 10 }}>{t('esign.legal')}</p>
    </Modal>
  );
}

/** on the client's documents tab: what was sent for signature and where it stands */
export function ESignList({ personId }: { personId: string }) {
  const rt = useRuntime();
  const { t, i18n } = rt;
  const a = useAccount();
  const [items, setItems] = useState<ESignInfo[] | null>(null);
  const load = () => { if (a?.signedIn) void accountService()?.api.esignList(personId).then((r) => setItems(r.items)).catch(() => setItems(null)); };
  useEffect(load, [personId, a?.signedIn]);
  if (!a?.signedIn) return null;
  return (
    <div style={{ padding: '0 20px 12px' }} data-testid="esign-list">
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <b className="small">{t('esign.list_title')}</b>
        <button className="btn btn-sm" onClick={() => rt.open(<SendForSignature personId={personId} />)} data-testid="esign-open"><Icon name="document" />{t('esign.send')}</button>
      </div>
      {items?.length ? <div className="agenda-list" style={{ marginTop: 6 }}>{items.map((e) => (
        <div key={e.id} className="agenda-item" style={{ gridTemplateColumns: 'minmax(0,1fr) auto' }}>
          <div><b>{e.title}</b> <span className={`chip ${e.status === 'signed' ? 'ok' : e.status === 'expired' || e.status === 'cancelled' ? '' : 'high'}`}>{t(`esign.status.${e.status}`)}</span>
            <div className="tiny muted">{t('esign.sent_on', { date: i18n.date(e.createdAt.slice(0, 10)) })}{e.signedAt ? ` · ${t('esign.signed_on', { date: i18n.date(e.signedAt.slice(0, 10)) })}` : ` · ${t('esign.until', { date: i18n.date(e.expiresAt.slice(0, 10)) })}`}</div></div>
          <span className="row" style={{ gap: 6 }}>
            {e.status === 'signed' && !rt.view.get('documents', signedDocumentId(e.id)) && <button className="btn btn-sm" onClick={() => void collectSigned(rt, e).then((ok) => { if (ok) rt.toast(t('esign.collected', { n: 1 }), 'success'); load(); })}>{t('esign.collect')}</button>}
            {(e.status === 'sent' || e.status === 'opened') && <button className="btn btn-sm btn-ghost" onClick={() => void accountService()!.api.esignCancel(e.id).then(load)}>{t('esign.cancel')}</button>}
          </span>
        </div>))}</div> : <p className="tiny muted">{t('esign.none')}</p>}
    </div>
  );
}
